/**
 * Sky, speaking for the platform's own assistant.
 *
 * `/v1/assist` answers from a fixed catalogue of hand-written queries, run as
 * the asking operator, with the rows attached. This module is the join between
 * that and the dock: it decides when a question belongs to the platform rather
 * than to the screen, and it turns an answer into something that can be read
 * aloud without losing the parts that make it checkable.
 *
 * Three things it refuses to smooth over.
 *
 * **A refusal is not an answer.** `render` returns null when the platform
 * refused, so the caller falls through to what Sky can read herself. Rendering
 * "I can't answer that" as an assistant turn would end the conversation on the
 * one case where she has more to offer, and rendering it as "nothing found"
 * would be a lie about an estate nobody looked at.
 *
 * **The inheritance is said first.** When a follow-up carried the previous
 * question forward, the platform writes the sentence that says so, and it goes
 * at the top where it is heard. An operator who asked about last month and is
 * being told about this week has about one sentence in which to notice.
 *
 * **The page is not the total.** When a result was truncated, the count in the
 * answer is what the question matched and the rows are what came back. Both
 * numbers are said, because "47" beside twenty lines invites exactly the wrong
 * arithmetic.
 */
import type { AssistReply, AssistRow } from "../domain/assist";
import type { Source } from "./types";

/**
 * A question that carries only the change — "and last month?", "what about 27?"
 *
 * Mirrors `assist/followup.py`, which does the real inheriting server-side.
 * The copy here decides something narrower and purely local: whether to take
 * this question to the platform *first*, ahead of the screen readings, because
 * a follow-up only means anything against the answer that preceded it. Getting
 * it wrong costs a lookup, not a wrong answer — the platform re-derives the
 * intent itself and states what it inherited.
 *
 * It is a copy, and copies drift: these patterns were already one revision
 * behind `followup.py` before the test below caught it. The drift is tolerable
 * precisely because the two sides do different jobs — this one only chooses an
 * order, the other one decides what a question means — so a stale copy here
 * costs a question taken to the platform second instead of first.
 */
const FOLLOW_UP = [
  /^\s*(and|what about|how about|now|then)\b/i,
  /^\s*(last|this|next)\s+(week|month|year|quarter|night|shift)\s*\??\s*$/i,
  /^\s*(yesterday|today|tonight)\s*\??\s*$/i,
  /^\s*(the )?(same|again)\b/i,
  /^\s*(just|only)\s+(the\s+)?\w+(\s+\w+){0,2}\s*\??\s*$/i,
  /^\s*\w+(\s+\w+){0,2}\s+only\s*\??\s*$/i,
];

export function looksLikeFollowUp(question: string): boolean {
  const q = question.trim();
  if (!q || q.length > 120) return false;
  return FOLLOW_UP.some((p) => p.test(q));
}

/** At most this many rows in the expanded report. Beyond it nobody is reading. */
const MAX_ROWS = 25;

/** The same ceiling shared out when an answer came from several queries. */
const PER_STEP_ROWS = 8;

/** Bookkeeping the platform returns about the query rather than the estate. */
const HIDDEN_KEYS = new Set(["id", "_total"]);

/**
 * One row, as a line.
 *
 * The catalogue's intents each return their own columns, so there is no shape
 * to map onto — the honest rendering is the row's own fields in the order the
 * query chose them, which is the order the author thought mattered. Ids are
 * dropped because a uuid in a spoken answer is noise, and every id here has a
 * label beside it that a person can actually use.
 */
function line(row: AssistRow): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(row)) {
    if (HIDDEN_KEYS.has(key) || value == null || value === "") continue;
    parts.push(typeof value === "object" ? `${key}: ${JSON.stringify(value)}` : `${key}: ${String(value)}`);
    if (parts.length === 5) break;
  }
  return parts.join(" · ");
}

export interface Rendered {
  /** What the dock shows. */
  text: string;
  /** What she says aloud, which is not the same thing. See `speakable`. */
  spoken: string;
  sources: Source[];
  more?: { label: string; lines: string[] };
}

/**
 * The same answer, for the ear.
 *
 * A model's summary wins when there is one: it says what the highlights say,
 * in a sentence, and the deterministic line stays on screen where a reader can
 * check the prose against it. With no summary the template is spoken instead,
 * with the notation that only works on a page taken back out — "47 open
 * defect(s)" is read aloud as "defect open bracket s close bracket" by every
 * speech engine there is.
 *
 * The inheritance note is always spoken, whichever branch runs. It is the one
 * sentence that lets an operator catch a question they did not ask, and an
 * operator listening rather than reading is exactly the one who cannot see it.
 */
function speakable(reply: AssistReply): string {
  const said: string[] = [];
  if (reply.inheritedNote) said.push(reply.inheritedNote.replace(/[\u201c\u201d]/g, ""));

  /* The model's sentence first, because it is the one that sounds like an
     answer. It is never the only thing said: it may not count and it may not
     rank, so every figure that decides anything is in the highlights below
     it, worked out over the whole result rather than the page in hand. */
  if (reply.answer.summary) said.push(reply.answer.summary);
  else said.push(reply.answer.text.replace(/\(s\)/g, "s"));

  for (const h of reply.answer.highlights) {
    said.push(
      h
        .replace(/^worst:/i, "The worst is")
        /* "Below" is a direction on a page and nowhere at all in the ear. */
        .replace(/the figures below describe/i, "and these figures describe")
        /* `{severity}×{count}` — severity first, which is the opposite of how
           it reads aloud. Getting this backwards had her say "3 at severity
           16" for sixteen severity-3 defects, and "5 at severity 1" for the
           single severity 5 that is the one worth hearing about. */
        .replace(/(\d+)\u00d7(\d+)/g, "$2 at severity $1")
        .replace(/\u00b7/g, ","),
    );
  }
  return said.join(" ");
}

/**
 * An answer, ready for the dock. Null when the platform refused — the caller
 * has better things to try than repeating a refusal.
 */
export function render(reply: AssistReply, siteCode: string): Rendered | null {
  if (reply.refused) {
    /* A declined judgement is an answer, and the only correct one. Returning
       null here sent "is the runway safe to open" on to the keyword search,
       which replied with two matching defect titles — the platform having
       refused, and Sky having answered anyway. */
    if (reply.refusalKind === "out_of_scope") {
      const said = refusal(reply);
      return { text: said, spoken: said, sources: [] };
    }
    return null;
  }

  const brief: string[] = [];
  if (reply.inheritedNote) brief.push(reply.inheritedNote);
  brief.push(reply.answer.text);
  for (const h of reply.answer.highlights) brief.push(`• ${h}`);
  /* The model's phrasing, when the deployment allows it one, and only after it
     has been checked against the rows. It sits below the deterministic
     sentence rather than replacing it, so drift is visible instead of
     authoritative. */
  if (reply.answer.summary) brief.push("", reply.answer.summary);
  if (reply.answer.narrationError) brief.push("", `(${reply.answer.narrationError}.)`);

  const sources: Source[] = reply.citations.map((c) => ({
    kind: "local",
    label: `${c.intent.replace(/_/g, " ")} · ${siteCode}`,
    detail:
      c.total > c.rowCount
        ? `${c.rowCount} of ${c.total} rows shown`
        : `${c.rowCount} row${c.rowCount === 1 ? "" : "s"}`,
  }));

  const rows = reply.citations.flatMap((c) => c.rows);
  /*
   * One heading per query, because an answer may now be several of them.
   *
   * The platform composes an answer from up to four catalogued queries, and
   * the records behind it were being flattened into one list under the LEAD
   * query's question. "What defects are open, and how bad are they?" sat over
   * defect rows and work-order rows together, which is a caption that is true
   * of the first few lines and false of the rest — the one shape of dishonesty
   * this whole module is arranged to avoid. Each block now says which query
   * produced the rows under it.
   */
  const blocks: string[] =
    reply.citations.length > 1
      ? reply.citations.flatMap((c, i) => [
          ...(i ? [""] : []),
          `${c.intent.replace(/_/g, " ")} — ${c.total > c.rowCount ? `${c.rowCount} of ${c.total}` : `${c.rowCount}`}`,
          ...c.rows.slice(0, PER_STEP_ROWS).map(line).filter(Boolean),
          ...(c.rows.length > PER_STEP_ROWS ? [`…and ${c.rows.length - PER_STEP_ROWS} more in the citation.`] : []),
        ])
      : [
          ...(reply.answers ? [reply.answers, ""] : []),
          ...rows.slice(0, MAX_ROWS).map(line).filter(Boolean),
          ...(rows.length > MAX_ROWS ? ["", `…and ${rows.length - MAX_ROWS} more in the citation.`] : []),
        ];
  const more = rows.length ? { label: "Show the records", lines: blocks } : undefined;

  return { text: brief.join("\n"), spoken: speakable(reply), sources, more };
}

/**
 * What she says when the platform refused.
 *
 * Used only where nothing else could answer either. It names two or three
 * things this deployment *can* be asked, because a refusal with no alternative
 * reads as a broken feature, and because the catalogue is otherwise invisible
 * — an operator has no way to learn its edges except by hitting them.
 */
export function refusal(reply: AssistReply): string {
  const declined = reply.refusalKind === "out_of_scope";
  const lines = declined
    ? [
        /* Not "I could not": she can see the readings perfectly well. This is
           a decision she is not entitled to make, and saying it any other way
           invites the operator to ask again in different words. */
        `I won't answer that — ${reply.refusalReason ?? "it asks for a judgement rather than a fact"}.`,
        "I can show you what the platform holds and you can decide.",
      ]
    : [
        reply.refusalReason
          ? `I could not look that up: ${reply.refusalReason}.`
          : "I could not look that up.",
        "Everything I say about this estate comes from the platform's own queries, so I would rather refuse than guess at a number.",
      ];
  const examples = reply.canAnswer.filter((c) => c.example).slice(0, 3);
  if (examples.length) {
    lines.push("", "I can answer things like:");
    for (const e of examples) lines.push(`• ${e.example}`);
  }
  return lines.join("\n");
}
